import { useCallback, useContext, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { LINKS } from '../../../../../../Routs/config';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
    CLEAR_STATE_KOSHER,
    LOAD,
    LOAD_END,
    SELECT_CLIENT_COSHER,
    SELECT_DRIVER_KOSHER,
} from '../../../../../../../store/actions/actionsTypes';
import { IPrinterData, SET_IS_OPEN } from '../../../../../../../store/reducers/printer';
import { RootState } from '../../../../../../../store/reducers/rootReducer';
import { ITeudotItem } from '../../../../../../../models/RequestsModel/teudot';
import { Pallets } from '../../../../../../modules/API/Pallets/pallets';
import { Certificates } from '../../../../../../modules/API/Certificates/certificates';
import { Defaults } from '../../../../../../modules/API/Defaults/defaults';
import { ICarItemResponse } from '../../../../../../modules/API/Defaults/models/cars';
import { IClientsItem } from '../../../../../../modules/API/Defaults/models/customers';
import { IPackagesItem } from '../../../../../../modules/API/Defaults/models/packages';
import { ISellerResponse } from '../../../../../../modules/API/Defaults/models/seller';
import { ITeudotTypeItem } from '../../../../../../modules/API/Defaults/models/teudotTypes';
import { IGetReuqestResponseNode } from '../../../../../../modules/API/Defaults/models/pallets';
import { IWarhouseListItem } from '../../../../../../modules/API/Defaults/models/warhouseList';
import { IPutItem } from '../../../../../../modules/API/Certificates/models/patchItem';
import { IUpdateProduct } from '../../../../../../modules/API/Market/models/marketRequests';
import { IPalletDetails } from '../../../../../../modules/API/Pallets/models/palletDetails';
import { PalletToChangeTypes } from '../../../../../../modules/API/Pallets/models/palletToChange';
import { ContextModal } from '../../../../../../modules/modal/modalContext';
import selectBrodMigdal from '../../../../../../generic/utils/selectBrodMigdal';
import { handleNotifications } from '../../../../../../generic/utils/handleNotifications';
import useCheckRights from '../../../../../../generic/hooks/useCheckRights';
import useDataFetcher from '../../../knisot/hooks/useFetchData';
import useDebounce from '../../../seller/Helpers/useDebounce';
import { useKnisaConfig } from '../../../knisot/Create/config';
import { useAddProductFormService } from '../../../market/Collect/services/hooks/useAddProductFormService';
import { ISelectPalletProps } from '../../../market/Collect/types/selectedPallet';
import { useProductCreationFieldsControl } from '../../../settings/FeatureControl/services/hooks/useProductCreationFieldsControl';
import useTabsTeuda from '../../generic/tabList';
import { useBarcodeScanner } from '../../../../../../generic/hooks/useBarcodeScanner';
import { useObjectImages } from '../../../../../../generic/hooks/useObjectImages';
import { buildTeudaImageRef } from '../../helpers/imageRef';
import { filterPallets } from '../../helpers/filterPallets';
import {
    prependPalletDetail,
    removePalletDetail,
    setPalletSelected,
    togglePalletExpanded,
} from '../../helpers/palletListUpdates';
import { IPalletEdited } from '../../types/palletItem';
import { ITeudotCreatUrlParams } from '../../types/params';
import ToastDurationButton from '../../../../../../generic/buttons/ToastDurationButton';
import React from 'react';

const useCreateTeuda = () => {
    const [t] = useTranslation(['teudot', 'buttons']);
    const { TAB_LIST } = useTabsTeuda();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const config = useKnisaConfig();

    const { id, subID } = useParams() as unknown as ITeudotCreatUrlParams;
    const titleTeuda: ITeudotItem = useSelector(
        (teuot: RootState) => teuot.kosherTeudotReducer.title,
    );
    const isEditMode = id !== 'new';

    const imageRef = useMemo(() => buildTeudaImageRef(id, subID), [id, subID]);
    const teudaImages = useObjectImages(imageRef);
    const showImagesToggle =
        teudaImages.images.length > 0 || teudaImages.comment.trim() !== '';
    const [isImagesOpen, setIsImagesOpen] = useState(false);
    const toggleImages = () => setIsImagesOpen((prev) => !prev);

    const { hasRight: isAllowToCreateTeudot } = useCheckRights({ key: 'R1002' });

    const { defaults } = useSelector((state: RootState) => state.defaults);
    const defaultCarID = selectBrodMigdal(defaults, 427, 'Num_Value');
    const rights = useSelector((state: RootState) => state.mainReducer.user?.rights);
    const codeInternal = useSelector(
        (state: RootState) => state.mainReducer.user?.code_internal,
    );
    const isCustomer = rights?.includes('CUSTOMER');
    const isWorker = rights?.includes('WORKER');

    const overweightDelta = selectBrodMigdal(defaults, 629, 'Num_Value');
    const defaultPackage = selectBrodMigdal(defaults, 382, 'Num_Value');
    const defaultArizaTranzit: number = useSelector(
        (item: RootState) =>
            item.defaults.defaults.filter((e: { Int_ID: number }) => e.Int_ID === 5)[0]
                ?.Num_Value,
    );
    const defaultTeudaType = selectBrodMigdal(defaults, 179, 'Num_Value', 'יציאה');
    const defaultPalletType: number = useSelector((item: RootState) =>
        item.defaults.defaults === null
            ? ''
            : item.defaults.defaults.filter(
                  (e: { Int_ID: number; Sub_ID: string }) => {
                      return e.Int_ID === 14 && e?.Sub_ID?.trim() === '1';
                  },
              )[0]?.Num_Value,
    );

    const defaultCreateState = useSelector(
        (state: RootState) => state.kosherTeudotReducer,
    );

    const packages = useSelector(
        (packages: RootState): IPackagesItem[] => packages.defaults.packageList,
    );

    const packageHash = useMemo(
        () =>
            packages.reduce((acc: Record<number, IPackagesItem>, cur) => {
                acc[cur.PackID] = cur;
                return acc;
            }, {}),
        [packages],
    );

    const clients = useSelector(
        (state: RootState): IClientsItem[] => state.defaults?.clientList?.list,
    );
    const printerState = useSelector(
        (state: RootState): IPrinterData => state.printerReducer,
    );
    const [modal, setModal] = useContext(ContextModal);
    const [activeTab, setActiveTab] = useState<string>(
        isEditMode ? TAB_LIST[0] : TAB_LIST[1],
    );

    const [date, setDate] = useState<string | null>(null);
    const [car, setCar] = useState<number>(
        defaultCreateState.create?.carInfo || defaultCarID || 0,
    );
    const [isOpenSelectDriver, setIsOpenSelectDriver] = useState(false);
    const [isOpenPalletObject, setIsOpenPalletObject] = useState(false);
    const [isOpenPalletToChangeModal, setIsOpenPalletToChangeModal] = useState(false);
    /*
     * In edit mode we pre-select the teuda's existing customer. In new
     * mode we always start with a blank selector — previously we fell
     * back to defaultCreateState.create.clientID from Redux, which kept
     * showing the last-used customer even when the user opened a fresh
     * "new teuda" route.
     */
    const [selectedClient, setSelectedClient] = useState<number | null>(
        isEditMode ? (titleTeuda?.CustomerID ?? null) : null,
    );

    const [palletList, setPalletList] = useState<IPalletEdited[] | null>(null);
    const [palletInput, setPalletInput] = useState<string>('');
    // Latest list for stable row callbacks (read from events, i.e. always after commit).
    const palletListRef = useRef<IPalletEdited[] | null>(null);
    useEffect(() => {
        palletListRef.current = palletList;
    }, [palletList]);
    const [tempPalletObject, setTempPalletObject] = useState<IPalletEdited | null>(null);
    const [isOpenSettings, setIsOpenSettings] = useState<boolean>(
        isEditMode ? false : true,
    );
    const [isOpenTransferTeudaModal, setIsOpenTransferTeudaModal] =
        useState<boolean>(false);
    const [sellerID, setSellerID] = useState<number>(titleTeuda?.Reseller_Code || 0);
    const [teudaType, setTeudaType] = useState<number>(defaultTeudaType);

    const [isOpenProductModal, setIsOpenProductModal] = useState<boolean>(false);
    const [selectedProduct, setSelectedProduct] = useState<IPalletDetails | null>(null);
    const [tranzitID, setTranzitID] = useState<number>(
        titleTeuda?.TranzitID || defaultArizaTranzit,
    );
    const [remark, setRemark] = useState<string>('');

    const { productCreationFields, productCreationFieldsDict } =
        useProductCreationFieldsControl();

    const { data: sellerList } = useDataFetcher<ISellerResponse>(
        Defaults.getSellerList,
        'data',
    );
    const { data: teudaTypeList } = useDataFetcher<ITeudotTypeItem[]>(
        Defaults.getTblTeudotTypes,
        'י',
    );

    const { data: carList } = useDataFetcher<ICarItemResponse>(
        Defaults.getCarList,
        'data',
    );

    const isAllowToEdit = useMemo(() => {
        let seller;
        if (rights?.includes('SELLER') && codeInternal) {
            seller = sellerList?.sellers?.find((e) => e.SellerID === +codeInternal);
        }
        return (
            (seller ? seller.EditDelivery : false) ||
            rights?.includes('ADMIN') ||
            rights?.includes('OFFICE') ||
            isAllowToCreateTeudot
        );
    }, [rights, sellerList, codeInternal, isAllowToCreateTeudot]);

    const clientName = useMemo(() => {
        return (
            clients
                ?.find((e) => e.CustomerID === selectedClient)
                ?.Customer_Name?.trim() || ''
        );
    }, [selectedClient, clients]);

    const getPallets = async () => {
        if (!selectedClient) return;
        dispatch({ type: LOAD });
        try {
            let res = await Pallets.getPalletListWithDetails(
                selectedClient || titleTeuda.CustomerID,
                tranzitID,
            );
            const resWithSelected = res.map((item: IPalletEdited) => {
                item.isSelected = false;
                return item;
            });

            if (isEditMode) {
                const resDetails: IPalletDetails[] =
                    await Pallets.getPalletsDetailsByDelivery(+id, +subID);
                const hash: Record<number, IPalletEdited> = {};

                resDetails.forEach((e) => {
                    if (!hash[e.Pallet_Num]) {
                        hash[e.Pallet_Num] = {
                            CustomerID: e.CustomerID,
                            Customer_Name: e.Customer_Name,
                            Details: [e],
                            Pallet_Date: e.Delivery_Date,
                            Pallet_Num: e.Pallet_Num,
                            isSelected: true,
                            InvoiceSale: e.InvoiceSale,
                        };
                    } else {
                        const data = { ...e };
                        const tempInvoice =
                            hash[e.Pallet_Num].InvoiceSale !== 0
                                ? hash[e.Pallet_Num].InvoiceSale || 0
                                : e.InvoiceSale;
                        data.InvoiceSale = tempInvoice;
                        hash[e.Pallet_Num].Details.push(data);
                    }
                });

                setPalletList([...Object.values(hash), ...resWithSelected]);
                return;
            }
            setPalletList(resWithSelected);
        } catch (err) {
            console.log(err);
        } finally {
            dispatch({ type: LOAD_END });
        }
    };

    const deleteProduct = useCallback(
        async (_pallet: number, recID: number) => {
            if (!isAllowToEdit) return;
            try {
                await Pallets.deleteProductFromPallet(recID);
                handleNotifications('המוצר נמחק בהצלחה', { type: 'success' });
            } catch (error: any) {
            } finally {
            }
        },
        [isAllowToEdit],
    );
    const delayedDeleteProduct = useCallback(
        async (pallet: number, recID: number) => {
            if (!isAllowToEdit) return;
            const itemFound = palletListRef.current
                ?.find((e) => e.Pallet_Num === pallet)
                ?.Details.find((e) => e.RecID === recID);
            if (!itemFound) return;
            setPalletList((prev) => removePalletDetail(prev, pallet, recID));

            let timerID: ReturnType<typeof setTimeout>;

            const undoDelete = () => {
                clearTimeout(timerID);
                toast.dismiss();
                setPalletList((prev) => prependPalletDetail(prev, pallet, itemFound));
            };
            const durationTime = 5000;
            toast.warning('פריט נמחק', {
                duration: durationTime - 500,
                cancel: React.createElement(ToastDurationButton, {
                    onClick: undoDelete,
                    duration: durationTime - 500,
                    lable: 'בטל',
                }),
            });

            timerID = setTimeout(async () => {
                toast.dismiss();
                await deleteProduct(pallet, recID);
            }, durationTime);
        },
        [isAllowToEdit, deleteProduct],
    );

    const selectClient = (id: number, name: string) => {
        dispatch({ type: SELECT_CLIENT_COSHER, clientID: id, clientName: name });
        setSelectedClient(id);
    };

    const handleControlSettings = () => {
        setIsOpenSettings(!isOpenSettings);
    };

    const handleCloseSettings = () => {
        if (!selectedClient || palletList === null) {
            dispatch({ type: CLEAR_STATE_KOSHER });
            navigate(-1);
            return;
        }
        setIsOpenSettings(false);
    };

    const handleConfirmSettings = () => {
        setIsOpenSettings(false);
        getPallets();
    };
    // Typing stays urgent; the ~4000-row list re-filters once per typing pause, as a deferred render.
    const debouncedPalletInput = useDebounce(palletInput, 300);
    const deferredPalletInput = useDeferredValue(debouncedPalletInput);
    const selectedTab = TAB_LIST[0];
    const renderPalletList = useMemo(
        () =>
            palletList
                ? filterPallets(palletList, deferredPalletInput, activeTab, selectedTab)
                : [],
        [palletList, deferredPalletInput, activeTab, selectedTab],
    );

    useEffect(() => {
        if (!selectedClient) {
            setSelectedClient(null);
            setPalletList(null);
        }
    }, [selectedClient]);

    useEffect(() => {
        if (isEditMode) getPallets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleControlPalletToChangeModal = () => {
        if (id === 'new' || !palletList?.length) return;
        setIsOpenPalletToChangeModal(!isOpenPalletToChangeModal);
    };
    const handleUnboundPallet = useCallback(
        async (id: number) => {
            if (!isEditMode || !isAllowToEdit) return;
            try {
                dispatch({ type: LOAD });
                await Certificates.unboundPallet(id);
            } catch (err) {
            } finally {
                dispatch({ type: LOAD_END });
            }
        },
        [isEditMode, isAllowToEdit, dispatch],
    );
    const handleSelectPallet = useCallback(
        async (palletNum: number) => {
            const current = palletListRef.current?.find((e) => e.Pallet_Num === palletNum);
            if (!current) return;
            const nextSelected = !current.isSelected;
            if (!nextSelected) await handleUnboundPallet(palletNum);
            setPalletList((prev) => setPalletSelected(prev, palletNum, nextSelected));
            setPalletInput('');
        },
        [handleUnboundPallet],
    );

    const selectDriver = (item: number) => {
        dispatch({ type: SELECT_DRIVER_KOSHER, payload: item });
        setCar(item);
    };

    const createTeuda = async () => {
        dispatch({ type: LOAD });
        const palletsArray = palletList?.reduce(
            (acc: number[], cur: IPalletEdited) => {
                if (cur.isSelected) {
                    acc.push(cur.Pallet_Num);
                }
                return acc;
            },
            [],
        );

        try {
            if (isEditMode) {
                const putData: IPutItem = {
                    PalletNums: palletsArray ?? [],
                    OrderNum: 0,
                    DeliveryNum: +id,
                    DeliverySub: +subID,
                };

                await Certificates.updateDeliveryByOrderOrPallet(putData);
            } else {
                const dataForTeuda = {
                    PalletNums: palletsArray || [],
                    OrderNum: 0,
                    CustomerID: selectedClient || 0,
                    DeliveryDate: date || '',
                    DeliveryTime: '',
                    Tranzit:
                        tranzitID || (config.create.title.tranzit[1].value as number),
                    Vehicle_Code: car,
                    SellerID: sellerID,
                    DeliveryType: teudaType,
                    Remarks: remark,
                };

                await Certificates.createCert(dataForTeuda);
            }
            setModal({
                open: true,
                value: 'תעודה נוצרה בהצלחה',
                mood: true,
            });
            setTimeout(() => {
                setModal({
                    ...modal,
                    open: false,
                });
            }, 3000);
            dispatch({ type: CLEAR_STATE_KOSHER });
            navigate(-1);
        } catch (err: any) {
            setModal({
                open: true,
                value: 'משהו השתבש',
                mood: false,
            });
            setTimeout(() => {
                setModal({
                    ...modal,
                    open: false,
                });
            }, 3000);
        } finally {
            dispatch({ type: LOAD_END });
        }
    };

    const getPalletByNum = async (value: string) => {
        dispatch({ type: LOAD });
        try {
            let res = await Pallets.getPalletWithDitailesByPalletNum(value);
            setTempPalletObject({
                ...res,
                isSelected: false,
            });
            setIsOpenPalletObject(true);
        } catch (err: any) {
            console.log(err.response?.data?.error);
        } finally {
            dispatch({ type: LOAD_END });
        }
    };

    const lookupPalletByValue = (value: string) => {
        if (!value) return;
        const numeric = +value;
        if (Number.isNaN(numeric)) return;
        const found = palletList?.find((item) => item.Pallet_Num === numeric);
        if (found) return;
        getPalletByNum(value);
    };

    const searchEnterPallet = () => {
        lookupPalletByValue(palletInput);
    };

    const savePalletinTheList = async () => {
        try {
            setPalletInput('');
            if (tempPalletObject) {
                let temp: IPalletEdited = {
                    ...tempPalletObject,
                    isSelected: true,
                    CustomerID: selectedClient || 0,
                };
                setPalletList((prev) => {
                    return [temp, ...(prev ?? [])];
                });
                setIsOpenPalletObject(false);
            }
        } catch (err) {}
    };

    const isSaveAllow = useMemo(() => {
        const finder = palletList?.find((e) => e.isSelected);
        return selectedClient && finder;
    }, [palletList, selectedClient]);

    // Lifted from PalletRow so an expanded row survives being virtualized out of view.
    const [expandedPallets, setExpandedPallets] = useState<ReadonlySet<number>>(() => new Set());
    const toggleExpanded = useCallback((palletNum: number) => {
        setExpandedPallets((prev) => togglePalletExpanded(prev, palletNum));
    }, []);

    const handleTabClick = (tab: string) => {
        if (tab === activeTab) return;
        setActiveTab(tab);
        // Rows used to remount collapsed on a tab switch — keep that.
        setExpandedPallets(new Set());
    };

    useBarcodeScanner({
        onScan: (value) => {
            setPalletInput(value);
            lookupPalletByValue(value);
        },
        duplicateScanWindowMs: 700,
    });

    const { data: dataPallets } = useDataFetcher<IGetReuqestResponseNode>(
        Pallets.getPalletList,
        'data',
    );

    const { data: dataTranzit } = useDataFetcher<IWarhouseListItem[]>(
        Defaults.getWarhouseList,
        'data',
    );

    const [isOpenAddModal, setIsOpenAddModal] = useState(false);
    const [selectedPallet, setSelectedPallet] = useState<ISelectPalletProps | null>(
        null,
    );

    const handleOpenAddModal = () => {
        setIsOpenAddModal(!isOpenAddModal);
    };

    const {
        qualityList,
        productList: dataProductsTypesWithID,
        growerList: dataGrowers,
        sizeData,
        getSizes,
        createProduct,
        updateProduct: serviceUpdateProduct,
    } = useAddProductFormService({
        warehouseID: tranzitID || 0,
        defaultPalletType,
        onCreateSuccess: (res, sent) => {
            if (!palletList) return;
            const newArr = [...palletList];
            const indexPallet = newArr.findIndex(
                (e) => e.Pallet_Num === res.palletNum,
            );
            if (indexPallet === -1) {
                const pallet = {
                    CustomerID: sent.CustomerID,
                    Customer_Name:
                        clients.find((e) => e.CustomerID === sent.CustomerID)
                            ?.Customer_Name || '',
                    Details: res.lines18,
                    Pallet_Date: format(new Date(), 'yyyyMMdd'),
                    Pallet_Num: res.palletNum,
                    Reseller_Code: 0,
                    Status: 0,
                };
                newArr.unshift(pallet);
            } else {
                newArr[indexPallet].Details = [...res.lines18];
            }
            setPalletList(newArr);
            handleNotifications('פריט נוסף בהצלחה', { type: 'success' });
            handleOpenAddModal();
        },
        onCreateError: () =>
            handleNotifications('שגיאה ביצירת המוצר', { type: 'error' }),
        onUpdateSuccess: (res) => {
            if (!selectedProduct) return;
            const recID = selectedProduct.RecID;
            const palletNum = selectedProduct.Pallet_Num;
            const updatedList = (palletList ?? []).map((item) => {
                if (item.Pallet_Num === palletNum) {
                    return {
                        ...item,
                        Details: item.Details.map((e) => (e.RecID === recID ? res : e)),
                    };
                }
                return item;
            });
            setPalletList(updatedList);
            handleControlProductModal();
        },
        onUpdateError: (error) => console.log(error),
    });

    const handleSelectForPallet = useCallback(
        (pallet: number, client: number, name: string, seller: number) => {
            if (!isAllowToEdit) return;
            setSelectedPallet({
                pallet,
                client,
                name,
                seller,
            });
            // Explicit open: a toggle would read a stale isOpenAddModal from this memoized closure.
            setIsOpenAddModal(true);
        },
        [isAllowToEdit],
    );
    const handleNewProduct = useCallback(
        (pallet: number) => {
            if (!isAllowToEdit) return;
            handleSelectForPallet(pallet, selectedClient || 0, '', 0);
        },
        [isAllowToEdit, selectedClient, handleSelectForPallet],
    );
    const handleNewPallet = () => {
        if (!isAllowToEdit) return;
        handleSelectForPallet(0, selectedClient || 0, '', 0);
    };

    const addProductModalProps = {
        data: selectedPallet,
        dataGrowers: dataGrowers,
        dataPallets: dataPallets,
        packageList: packages,
        defaultPallet: defaultPalletType,
        defaultPackage: defaultPackage,
        createProduct: createProduct,
        cashProduct: () => {},
        dataProductsWithVariety: dataProductsTypesWithID,
        getSizes: getSizes,
        sizeData: sizeData,
        isWorker: isWorker,
        overweightDelta: overweightDelta,
        qualityList: qualityList || [],
        productCreationFields: productCreationFields,
        productCreationFieldsDict: productCreationFieldsDict,
    };

    const settingsModalProps = {
        carList: carList?.cars,
        carID: car,
        setCarID: selectDriver,
        sellerList: sellerList?.sellers,
        sellerID: sellerID,
        setSellerID: setSellerID,
        teudaTypeList: teudaTypeList,
        teudaType: teudaType || defaultTeudaType,
        setTeudaType: setTeudaType,
        selectedClient: selectedClient,
        setSelectedClient: selectClient,
        handleClose: handleConfirmSettings,
        tranzitList: dataTranzit,
        tranzitID: tranzitID,
        setTranzitID: setTranzitID,
        remark: remark,
        setRemark: setRemark,
    };

    const handleSelectProduct = useCallback(
        (item: IPalletDetails) => {
            if (!isAllowToEdit) return;
            setSelectedProduct(item);
            setIsOpenProductModal(true);
        },
        [isAllowToEdit],
    );

    function handleControlProductModal() {
        setIsOpenProductModal((v) => !v);
    }

    const updateProduct = async (data: IUpdateProduct) => {
        if (!selectedProduct || !isAllowToEdit) return;
        await serviceUpdateProduct(selectedProduct.RecID, data);
    };

    const printPallet = useCallback(
        async (palletNum: number) => {
            if (isCustomer) return;
            if (!printerState.printerID) {
                dispatch({ type: SET_IS_OPEN, payload: true });
                return;
            }
            dispatch({ type: LOAD });
            try {
                const data = {
                    ZPL_15_75: 3,
                    PalletEntryNum: +palletNum,
                    PalletZPLFormat: +(printerState.formatID ?? 0),
                    ChangeInternationalEncodingZPL: printerState.encoding,
                };
                const res = await Pallets.printPalletZPL(printerState.printerID, data);
                if (typeof res !== 'number') {
                    const modified = (res as string).split('.il').join('.il:9086');
                    window.open(modified);
                } else {
                    handleNotifications('הדפסה בוצעה בהצלחה', { type: 'success' });
                }
            } catch {
                handleNotifications('שגיאה בהדפסת המשטח', { type: 'error' });
            } finally {
                dispatch({ type: LOAD_END });
            }
        },
        [isCustomer, printerState, dispatch],
    );

    const PropsForPalletItem = {
        handleSelectPallet: handleSelectPallet,
        packageHash,
        addProduct: handleNewProduct,
        handleSelectProduct: handleSelectProduct,
        deleteProduct: delayedDeleteProduct,
        isAllowToEdit: isAllowToEdit,
        printPallet: printPallet,
    };
    const selectedProductModalProps = {
        data: selectedProduct,
        updateProduct: updateProduct,
        pallets: dataPallets?.pallets || [],
        growers: dataGrowers?.growers || [],
        isWorker: false,
        packageList: packages,
        dataProductsWithVariety: dataProductsTypesWithID,
        getSizes: getSizes,
        sizeData: sizeData,
        qualityList: qualityList || [],
    };
    const selectDriverP = {
        saveFunc: selectDriver,
        list: carList,
        defaultCar: car,
        isFullObject: true,
    };
    const palletToChangeProps = {
        id: +id,
        type: PalletToChangeTypes.DELIVERY,
        saveFunc: handleControlPalletToChangeModal,
        palletTypeList: dataPallets?.pallets || [],
    };

    const navigateBack = () => {
        navigate(LINKS.teudot.teudotMishloah.mainURL);
    };

    return {
        t,
        id,
        subID,
        isEditMode,
        titleTeuda,
        clientName,
        navigateBack,
        isAllowToEdit,
        isSaveAllow,
        selectedClient,
        activeTab,
        handleTabClick,
        expandedPallets,
        toggleExpanded,
        setDate,
        palletInput,
        setPalletInput,
        renderPalletList,
        searchEnterPallet,
        handleNewPallet,
        handleControlSettings,
        handleCloseSettings,
        handleControlPalletToChangeModal,
        createTeuda,
        // modal flags & helpers
        isOpenSelectDriver,
        setIsOpenSelectDriver,
        isOpenPalletObject,
        setIsOpenPalletObject,
        isOpenPalletToChangeModal,
        isOpenSettings,
        isOpenTransferTeudaModal,
        setIsOpenTransferTeudaModal,
        isOpenAddModal,
        handleOpenAddModal,
        isOpenProductModal,
        handleControlProductModal,
        tempPalletObject,
        savePalletinTheList,
        selectedProduct,
        selectedPallet,
        // bags
        PropsForPalletItem,
        settingsModalProps,
        addProductModalProps,
        selectedProductModalProps,
        selectDriverP,
        palletToChangeProps,
        // teuda images
        teudaImages,
        showImagesToggle,
        isImagesOpen,
        toggleImages,
    };
};

export default useCreateTeuda;
